import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Download, Loader2, Check, Info } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { isIOS, sharePng, downloadPng, pngFile, canShareFile } from "@/lib/shareSticker";

const STEPS_ANDROID = [
  "Toque em \u201CEnviar para o Instagram\u201D e escolha Instagram \u2192 Stories, ou toque em \u201CSalvar na galeria\u201D.",
  "Se salvou: abra os Stories e toque no adesivo (carinha quadrada) \u2192 \u201CFoto\u201D \u2192 escolha a figurinha.",
  "Arraste e ajuste o tamanho como quiser. O fundo continua transparente.",
];
const STEPS_IOS = [
  "Toque em \u201CEnviar para o Instagram\u201D e escolha \u201CSalvar imagem\u201D (vai para Fotos) ou o Instagram.",
  "Nos Stories, toque no adesivo (carinha quadrada) \u2192 \u201CFoto\u201D \u2192 escolha a figurinha.",
  "Arraste e ajuste o tamanho como quiser. O fundo continua transparente.",
];

const STEP_SAVE_ONLY = "Toque em \u201CSalvar na galeria (PNG)\u201D \u2014 a figurinha vai para a galeria/Downloads do celular.";

// Alternatives when pasting into Instagram isn't possible on this phone/browser
export function StickerHelpSheet({ open, onOpenChange, stickerId, blob, fileName, copied }) {
  const [busy, setBusy] = useState(false);
  const shareable = !!blob && canShareFile(pngFile(blob, fileName));
  const base = isIOS ? STEPS_IOS : STEPS_ANDROID;
  const steps = shareable ? base : [STEP_SAVE_ONLY, ...base.slice(1)];

  const share = async () => {
    setBusy(true);
    try {
      if (!(await sharePng(blob, fileName))) toast("Seu navegador não abre o menu de compartilhar. Use \u201CSalvar na galeria\u201D \uD83D\uDC97");
    } catch (e) {
      toast("Não deu para compartilhar. Use \u201CSalvar na galeria\u201D \uD83D\uDC97");
    } finally { setBusy(false); }
  };

  const save = () => {
    try {
      downloadPng(blob, fileName);
      toast.success("Figurinha salva em PNG! \uD83D\uDC97");
    } catch (e) {
      toast.error("Não foi possível salvar. Tente novamente.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`sticker-help-${stickerId}`} className="w-[calc(100%-1.5rem)] max-w-md rounded-[2rem] border-[#F2E6E7] bg-white p-5 sm:p-6 gap-0">
        <DialogTitle className="font-serif text-2xl font-normal pr-8">Usar no Instagram</DialogTitle>
        <DialogDescription className="text-sm text-[#7A7571] mt-1.5" data-testid={`sticker-help-status-${stickerId}`}>
          {copied
            ? "Copiamos a figurinha. Se o Instagram não mostrar \u201CColar\u201D no seu celular, use uma das opções abaixo."
            : "Seu celular não permitiu copiar a imagem. Use uma das opções abaixo \u2014 funciona em qualquer aparelho."}
        </DialogDescription>
        <div className="mt-5 flex flex-col gap-2">
          {shareable && (
            <button onClick={share} disabled={busy || !blob} data-testid={`sticker-help-share-${stickerId}`} className="h-12 rounded-full bg-[#2C2A29] text-white font-bold flex items-center justify-center gap-2 transition-[background-color,transform] duration-200 hover:bg-black active:scale-[0.97] disabled:opacity-70">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Share2 size={17} />} Enviar para o Instagram
            </button>
          )}
          <button onClick={save} disabled={!blob} data-testid={`sticker-help-download-${stickerId}`} className={`h-12 rounded-full font-bold flex items-center justify-center gap-2 transition-[background-color,transform] duration-200 active:scale-[0.97] disabled:opacity-70 ${shareable ? "bg-[#F7EBEC] text-[#B07A7F] hover:bg-[#EFD9DB]" : "bg-[#2C2A29] text-white hover:bg-black"}`}>
            <Download size={17} /> Salvar na galeria (PNG)
          </button>
        </div>
        <div className="mt-5 rounded-2xl bg-[#FBF5F5] p-4">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#B07A7F]"><Info size={13} /> Como colocar no Story</p>
          <ol className="mt-2 space-y-1.5 text-xs text-[#5C5755] list-decimal pl-4" data-testid={`sticker-help-steps-${stickerId}`}>
            {steps.map((s) => <li key={s}>{s}</li>)}
          </ol>
        </div>
        <button onClick={() => onOpenChange(false)} data-testid={`sticker-help-close-${stickerId}`} className="mt-4 mx-auto flex items-center gap-1.5 text-sm font-semibold text-[#7A7571] hover:text-[#2C2A29] transition-colors">
          <Check size={15} /> Entendi
        </button>
      </DialogContent>
    </Dialog>
  );
}
